
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import UserGroup from '@/models/UserGroup';
import moment, { Moment } from 'moment';
import { getDatepickerLocale } from '@/helpers/LocaleHelper';
import UserGroupRepository from '@/repositories/UserGroupRepository';
import { downloadTableAsExcel, ExportColumn } from '@/helpers/PrintHelper';

@Component({
    name: 'CmsB2BGroupsActivityTable',
})
export default class CmsB2BGroupsActivityTable extends Vue {
    private search: string = '';

    private isLoading: boolean = false;
    private dateRange: [Moment, Moment] = [moment().subtract(30, 'days'), moment()];

    private get userGroups() {
        return UserGroupRepository.getAll();
    }

    private get locale() {
        return getDatepickerLocale();
    }

    private get columns() {
        return [
            {
                title: this.$t('ID'),
                dataIndex: 'id',
                sortDirections: ['descend', 'ascend'],
                sorter: (a: any, b: any) => a.id.localeCompare(b.id),
                width: '20%',
            },
            {
                title: this.$t('Name'),
                dataIndex: 'name',
                sortDirections: ['descend', 'ascend'],
                sorter: (a: any, b: any) => a.name.localeCompare(b.name),
                width: '50%',
            },
            {
                title: 'RApp ' + this.$t('Orders'),
                dataIndex: 'roltekOrders',
                sortDirections: ['descend', 'ascend'],
                sorter: (a: any, b: any) => a.roltekOrders - b.roltekOrders,
                width: '15%',
            },
            {
                title: this.$t('Imported orders'),
                dataIndex: 'b2bOrders',
                sortDirections: ['descend', 'ascend'],
                sorter: (a: any, b: any) => a.b2bOrders - b.b2bOrders,
                width: '15%',
            },
        ];
    }

    private get filteredList(): any[] {
        if (!this.userGroups || !this.userGroups.length) {
            return [];
        }
        return this.userGroups
            .filter((group: any) => {
                return group.name.toLowerCase().includes(this.search.toLowerCase());
            })
            .map((group: any) => ({
                id: group.id,
                name: group.name,
                roltekOrders: group.roltekAppOrdersCount || 0,
                b2bOrders: group.b2bGroupsOrderCount || 0,
            }));
    }

    private get total(): number {
        return this.filteredList.length;
    }

    private async onDateChange() {
        await this.fetchUserGroups();
    }

    private downloadExcel() {
        const tableData = this.filteredList;
        const tableColumns = this.columns as ExportColumn[];

        if (!tableData || tableData.length === 0) {
            return;
        }

        downloadTableAsExcel(tableColumns, tableData, 'B2B_Groups_Activity');
    }

    private async fetchUserGroups() {
        this.isLoading = true;
        try {
            const startDateStr = this.dateRange[0].format('YYYY-MM-DD');
            const endDateStr = this.dateRange[1].format('YYYY-MM-DD');

            await UserGroup.getAllByPeriod(startDateStr, endDateStr);
        } catch (error) {
            console.error('Error fetching user groups:', error);
            this.$message.error(this.$t('Error loading B2B groups') as string);
        } finally {
            this.isLoading = false;
        }
    }

    private async mounted() {
        await this.fetchUserGroups();
    }
}
